import { Data } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { IReport, ReportingTypes, ReportViewComponentVM } from '@singularsystems/neo-reporting';
import { NotificationDuration } from '../../../../App/Models/NotificationDuration';
import DividendLetterReport from '../../../../Reporting/Reports/DividendLetter/DividendLetterReport';
import InterCompanyDividendLetterReport from '../../../../Reporting/Reports/InterCompanyDividendLetter/InterCompanyDividendLetterReport';
import PortfolioStatementReport from '../../../../Reporting/Reports/PortfolioStatement/PortfolioStatementReport';
import { AppService, DomainTypes, Types } from '../../../DomainTypes';
import ExportLetterCommand from '../../../Models/LetterAuthorisation/Commands/ExportLetterCommand';
import LetterAuthorisationCriteria from '../../../Models/LetterAuthorisation/Commands/LetterAuthorisationCriteria';
import LetterAuthorisationSignatory from '../../../Models/LetterAuthorisation/LetterAuthorisationSignatory';
import LetterAuthorisationBatchLookup from '../../../Models/LetterAuthorisation/Lookups/LetterAuthorisationBatchLookup';
import LetterAuthorisationLookup from '../../../Models/LetterAuthorisation/Lookups/LetterAuthorisationLookup';
import LetterAuthorisationSignatureLookup from '../../../Models/LetterAuthorisation/Lookups/LetterAuthorisationSignatureLookup';
import { ReportType } from '../../../Models/LetterAuthorisation/ReportType';
import * as Roles from '../../../Models/Security/Roles';
import IssuerDividendLetterReport from "../../../../Reporting/Reports/IssuerDividendLetter/IssuerDividendLetterReport";
import BankTransferLetterReport from "../../../../Reporting/Reports/BankTransferLetter/BankTransferLetterReport";
import BankTransferLetterCapitalReport from "../../../../Reporting/Reports/BankTransferLetterCapital/BankTransferLetterCapitalReport";
import { runInAction } from "mobx";

export default class AuthorisationBatchVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private onClose: (batchId: number) => void,
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public apiClient = AppService.get(DomainTypes.ApiClients.LetterAuthorisation),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        private reportingService = AppService.get(ReportingTypes.Services.ReportingService),
        public authorisation = AppService.get(Types.Neo.Security.AuthorisationService),
        private authenticationService = AppService.get(Types.Neo.Security.AuthenticationService),
        private documentApiClient = AppService.get(DomainTypes.ApiClients.Documents)) {

        super(taskRunner);
        this.makeObservable();

        this.autoDispose(this.letterCriteria.onAnyPropertyChanged(() => this.pageManager.refreshData()));
    }

    public batch: LetterAuthorisationBatchLookup | null = null;
    private onChanged?: () => void;

    public letterCriteria = new LetterAuthorisationCriteria();
    public pageManager = new Data.PageManager(this.letterCriteria, LetterAuthorisationLookup, this.apiClient.getLetterAuthorisationLookupsPaged,
        { pageSize: 50 });

    public exportCommand: ExportLetterCommand | null = null;

    public requiresRecordToBeAuthorised: boolean = false;

    public async initialise() {
        
    }

    public async loadAuthorisationBatch(batchId: number, onChanged: () => void) {
        this.onChanged = onChanged;

        const [batchResult, signatories, config] = await this.taskRunner.waitFor(Promise.all(
            [
                this.apiClient.getLetterAuthorisationBatchLookup(batchId),
                this.dataCache.letterAuthorisationSignatories.getDataAsync(),
                this.dataCache.tenantConfig.getDataAsync()
            ]));

        this.batch = LetterAuthorisationBatchLookup.fromJSObject<LetterAuthorisationBatchLookup>(batchResult.data);
        this.batch.setSignatories(signatories);

        this.requiresRecordToBeAuthorised = config.authorisationRequiredForReporting;

        runInAction(() => {
            this.letterCriteria.letterAuthorisationBatchId = batchId;
            this.letterCriteria.reportType = this.batch!.reportType;
        });
    }

    public async addUnlinkedRecordsToBatch() {
        if (this.batch) {
            await this.taskRunner.run(() => this.apiClient.addUnlinkedRecordsToBatch(this.batch!.letterAuthorisationBatchId));
            this.batch.unlinkedRecordCount = 0;
            this.notifications.addSuccess("Add Unlinked", "Unlinked records have been added to the batch.", NotificationDuration.Standard);
            this.pageManager.refreshData();
        }
    }

    public authoriseBatch() {

        if (this.batch) {
            if (this.requiresRecordToBeAuthorised && this.batch.hasUnauthorisedRecords) {
                this.notifications.addDanger("Authorise", "Cannot authorise, some linked records are unauthorised.", NotificationDuration.Standard);
                return;
            }

            this.taskRunner.run(() => this.apiClient.authoriseBatch(this.batch!.letterAuthorisationBatchId));
            this.notifications.addSuccess("Authorise", "Batch has been authorised successfully", NotificationDuration.Standard);
            this.batch.isAuthorised = true;
            this.onChanged!();
        }
    }

    public async unauthoriseBatch() {
        if (this.batch) {
            await this.taskRunner.run(() => this.apiClient.unAuthoriseBatch(this.batch!.letterAuthorisationBatchId));
            this.notifications.addSuccess("Re-Open", "Batch has been re-opened successfully", NotificationDuration.Standard);
            this.batch.isAuthorised = false;
            this.onChanged!();
        }
    }

    public beginExport(letter?: LetterAuthorisationLookup) {

        if (this.batch) {
            const command = new ExportLetterCommand();
            command.letterAuthorisationBatchId = this.batch.letterAuthorisationBatchId;
            
            if (letter) {
                command.letterAuthorisationId = letter.letterAuthorisationId;
                command.hasSent = letter.sentOn !== null;
                command.emailAddress = letter.emailAddress ?? "";
                command.isLetterPasswordProtected = letter.isLetterPasswordProtected;
                command.letterPassword = letter.letterPassword ?? "";

            } else {
                command.hasSent = this.batch.sentCount > 0;
            }

            this.exportCommand = command;
        }
    }

    public doExport() {
        return this.taskRunner.run(async () => {
            if (this.exportCommand) {

                await this.apiClient.exportLetters(this.exportCommand.toJSObject());

                this.notifications.addSuccess("Export", "Letter(s) have been queued for export. This process will run in the background.", NotificationDuration.Standard);
                this.exportCommand = null;
                this.onChanged!();
            }
        });
    }

    public saveAsDocument(letter?: LetterAuthorisationLookup) {
        return this.taskRunner.run(async () => {
            
            await this.apiClient.saveLetters({ 
                letterAuthorisationBatchId: this.batch!.letterAuthorisationBatchId, 
                letterAuthorisationId: letter?.letterAuthorisationId });

            this.notifications.addSuccess("Save", "Letter(s) have been queued to generate. This process will run in the background.", NotificationDuration.Standard);
            this.onChanged!();
        })
    }

    public async downloadDocument(letter: LetterAuthorisationLookup) {
        if (letter.fileId && letter.investorId) {
            const downloadUrl = await this.taskRunner.waitForData(this.documentApiClient.getInvestorFileUrl(letter.fileId, letter.investorId));

            window.open(downloadUrl, "Letter", "width=800,height=800");
        }
    }

    // View Report

    public reportViewVM: ReportViewComponentVM | null = null;
    public letterInfo: { letter: LetterAuthorisationLookup, signatoriesAllowed: LetterAuthorisationSignatory[] } | null = null;

    public async viewReport(letter: LetterAuthorisationLookup) {

        this.letterInfo = {
            letter,
            signatoriesAllowed : this.getSignatoriesAllowedToSign(letter)
        };

        const report = (await this.getReport(letter, this.batch!)) as IReport;        

        const reportViewVM = this.registerViewModel(new ReportViewComponentVM(report, undefined, this.closeReport.bind(this)));
        reportViewVM.zoomLevel = 1;
        await this.taskRunner.waitFor(reportViewVM.load());
        
        this.reportViewVM = reportViewVM;
    }

    public closeReport() {
        this.disposeViewModel((this as AuthorisationBatchVM).meta.reportViewVM);
    }

    public async signReport(signatory: LetterAuthorisationSignatory) {

        if (this.letterInfo) {
            if (signatory.hasSigned) {
                await this.rejectReport(this.letterInfo.letter, signatory);
            } else {
                await this.authoriseReport(this.letterInfo.letter, signatory);
            }

            this.reportViewVM?.hide();
        }
    }

    public async deleteLetter(letter: LetterAuthorisationLookup) {
        await this.taskRunner.run(() => this.apiClient.deleteLetter(letter.letterAuthorisationId));
        this.pageManager.data.removeWithoutTracking(letter);
        if (this.pageManager.data.length === 0) {
            this.onClose(this.batch!.letterAuthorisationBatchId);
        }
    }

    private async authoriseReport(letter: LetterAuthorisationLookup, signatory: LetterAuthorisationSignatory) {

        const result = await this.taskRunner.run(() => this.apiClient.authoriseLetter({ 
            letterAuthorisationId: letter.letterAuthorisationId, 
            signatoryId: signatory.letterAuthorisationSignatoryId }));

        letter.signatures.push(LetterAuthorisationSignatureLookup.mapFrom<LetterAuthorisationSignatureLookup>({
            signatoryId: signatory.letterAuthorisationSignatoryId,
            signatoryName: signatory.display,
        }));

        if (result.data.isComplete) {
            letter.isComplete = true;
            this.batch!.completeCount += 1;
            this.onChanged!();
        }
    }

    private async rejectReport(letter: LetterAuthorisationLookup, signatory: LetterAuthorisationSignatory) {

        await this.taskRunner.run(() => this.apiClient.unAuthoriseLetter({ 
            letterAuthorisationId: letter.letterAuthorisationId, 
            signatoryId: signatory.letterAuthorisationSignatoryId }));

        const signature = letter.signatures.find(c => c.signatoryId === signatory.letterAuthorisationSignatoryId);
        if (signature) {
            letter.signatures.remove(signature);
            if (letter.isComplete) {
                this.batch!.completeCount -= 1;
                letter.isComplete = false;
            }
            this.onChanged!();
        }
    }

    private getReport(letter: LetterAuthorisationLookup, batch: LetterAuthorisationBatchLookup) {
        if (batch.reportType === ReportType.InvestorDividendLetter) {
            return this.getInvestorDividendReport(letter, batch);
        }
        else if (batch.reportType === ReportType.IssuerDividendLetter) {
            return this.getIssuerDividendReport(letter, batch);
        }
        else if (batch.reportType === ReportType.PortfolioStatement) {
            return this.getPortfolioStatementReport(letter, batch);
        }
        else if (batch.reportType === ReportType.BankTransferFormDividend) {
            return this.getBankTransferFormReport(letter, batch);
        }
        else if (batch.reportType === ReportType.InterCompanyDividendLetter) {
            return this.getInterCompanyDividendLetterReport(letter, batch);
        }
        else if (batch.reportType === ReportType.BankTransferFormCapital) {
            return this.getBankTransferFormCapitalReport(letter, batch);
        }
        throw Error("Unknown Report Type");
    }

    private getInvestorDividendReport(letter: LetterAuthorisationLookup, batch: LetterAuthorisationBatchLookup) {

        return this.taskRunner.waitFor(
            this.reportingService.createReport(
                DividendLetterReport, 
                { dividendAccrualId: letter.dividendAccrualId, dividendDate: batch.reportDate, entityId: batch.entityId }));
    }

    private getIssuerDividendReport(letter: LetterAuthorisationLookup, batch: LetterAuthorisationBatchLookup) {

        return this.taskRunner.waitFor(
            this.reportingService.createReport(
                IssuerDividendLetterReport, 
                { dividendAccrualId: letter.dividendAccrualId, dividendDate: batch.reportDate, entityId: batch.entityId }));
    }

    private getPortfolioStatementReport(letter: LetterAuthorisationLookup, batch: LetterAuthorisationBatchLookup) {

        return this.taskRunner.waitFor(
            this.reportingService.createReport(
                PortfolioStatementReport, 
                { investorId: letter.investorId, startDate: batch.reportStartDate, endDate: batch.reportDate, entityId: batch.entityId }));
    }

    private getBankTransferFormReport(letter: LetterAuthorisationLookup, batch: LetterAuthorisationBatchLookup) {

        return this.taskRunner.waitFor(
            this.reportingService.createReport(
                BankTransferLetterReport, 
                { dividendAccrualId: letter.dividendAccrualId, entityId: batch.entityId }));
    }

    private getBankTransferFormCapitalReport(letter: LetterAuthorisationLookup, batch: LetterAuthorisationBatchLookup) {

        return this.taskRunner.waitFor(
            this.reportingService.createReport(
                BankTransferLetterCapitalReport, 
                { 
                    entityId: batch.entityId, 
                    reportDate: batch.reportDate, 
                    investorId: letter.investorId, 
                    investorReferralTypeId: letter.investorReferralTypeId, 
                    inOnly: letter.boolFilter
                }));
    }
    
    private getInterCompanyDividendLetterReport(letter: LetterAuthorisationLookup, batch: LetterAuthorisationBatchLookup) {

        return this.taskRunner.waitFor(
            this.reportingService.createReport(
                InterCompanyDividendLetterReport, 
                { investorId: letter.investorId, dividendDate: batch.reportDate, entityId: batch.entityId }));
    }

    /** 
     * Gets signatories required for this batch, that haven't already signed the letter.
     * Will also remove user signatories not linked to the current user.
     */
    private getSignatoriesAllowedToSign(letter: LetterAuthorisationLookup) {
        const hasAuthRole = this.authorisation.hasRole(Roles.LetterAuthorisation.AuthoriseLetter);
        const signatories: LetterAuthorisationSignatory[] = [];

        const user = this.authenticationService.user!;

        for (let signatory of this.batch!.requiredSignatories) {

            const alreadySigned = letter.signatures.find(c => c.signatoryId == signatory.signatoryId);

            if (signatory.model) {
                signatory.model.hasSigned = alreadySigned !== undefined;
                let allowedToSign = false;

                if (signatory.model.userName) {
                    // Must match current user.
                    if (signatory.model.userName.toUpperCase() === user.userName.toUpperCase()) {
                        allowedToSign = true;
                    }
                } else if (hasAuthRole) {
                    allowedToSign = true;
                }

                signatory.model.allowedToSign = allowedToSign;

                signatories.push(signatory.model);
            }
        }

        return signatories;
    }
}